import { EPDProductClassificationId } from 'components/v2/compiler/constants';
import DangerMaterialsGridPdf from 'components/v2/compiler/grids/product-content/DangerMaterialsGridPdf';
import OtherDangerMaterialsGridPdf from 'components/v2/compiler/grids/product-content/OtherDangerMaterialsGridPdf';
import PackagingMaterialsGridPdf from 'components/v2/compiler/grids/product-content/PackagingMaterialsGridPdf';
import PanelContentGridPdf from 'components/v2/compiler/grids/product-content/PanelContentGridPdf';
import ProductComponentsGridPdf from 'components/v2/compiler/grids/product-content/ProductComponentsGridPdf';
import { PcrV2Context } from 'components/v2/compiler/hooks/usePcrV2';
import { IsISO21930StandardCompliant, IsPanelContentEnabled } from 'components/v2/compiler/utils';
import CompareEnvironmentPerformanceSection from 'components/v2/compiler/version-compare/CompareEnvironmentPerformanceSection';
import CompareLayout from 'components/v2/compiler/version-compare/CompareLayout';
import CompareLcaInformationSection from 'components/v2/compiler/version-compare/CompareLcaInformationSection';
import CompareProductsSection from 'components/v2/compiler/version-compare/CompareProductsSection';
import CompareScenariosSection from 'components/v2/compiler/version-compare/CompareScenariosSection';
import CompareSystemBoundarySection from 'components/v2/compiler/version-compare/CompareSystemBoundarySection';
import CompareTechnicalPerformanceSection from 'components/v2/compiler/version-compare/CompareTechnicalPerformanceSection';
import EpdComparedVersionHistoryDataContext from 'components/v2/compiler/version-compare/EpdComparedVersionHistoryDataContext';
import VersionCompareKeyValueGrid from 'components/v2/compiler/version-compare/VersionCompareKeyValueGrid';
import { VersionCompareRow, VersionCompareSection } from 'components/v2/compiler/version-compare/VersionCompareRow';
import { getContentDeclarationHeaderRows } from 'components/v2/compiler/version-history/content/VersionHistoryContentDeclarationSection';
import EpdVersionHistoryDataContext from 'components/v2/compiler/version-history/utils/EpdVersionHistoryDataContext';
import { VersionHistorySummaryGridSkeleton } from 'components/v2/compiler/version-history/VersionHistorySummaryGrid';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { FieldPanelHeader, FormPanel } from 'styles/v2/Styles.styled';
import { EPDContentDeclarationProductType } from 'types/content-declaration/EPDContentDeclarationProductType';
import isFalsyOrEmptyHtmlRichText from 'util/isFalsyOrEmptyHtmlRichText';
import CompareGeneralInformationSection from '../../../components/v2/compiler/version-compare/CompareGeneralInformationSection';
import { withGreyBackground } from '../WithGreyBackground';
import ComparePanelComponent, { CompareDiv } from './ComparePanelComponent';
import { useEpdVersionData } from './useEpdVersionData';
import CompareEnvironmentPerformanceCustomSection from 'components/v2/compiler/version-compare/CompareEnvironmentPerformanceCustomSection';

interface RouteParams {
  epdId: string;
  versionAId: string;
  versionBId: string;
}

const EpdCompilerVersionCompare: React.FC<{}> = () => {
  const { epdId, versionAId, versionBId } = useParams<RouteParams>();
  const [fullWidth, setFullWidth] = useState(false);
  const { dataForContext: left, isShowingLoadingSkeleton: isLeftLoading } = useEpdVersionData(versionAId);
  const { dataForContext: right, isShowingLoadingSkeleton: isRightLoading } = useEpdVersionData(versionBId);
  const data = { left: left, right: right };

  if (isLeftLoading || isRightLoading) {
    return (
      <CompareLayout fullWidth={fullWidth} setFullWidth={setFullWidth} epdId={epdId}>
        <CompareDiv>
          <VersionHistorySummaryGridSkeleton skeletonRowsCount={20} />
          <VersionHistorySummaryGridSkeleton skeletonRowsCount={20} />
        </CompareDiv>
      </CompareLayout>
    );
  }

  const averageProductsContentDeclaration = getProductPairByType(data, EPDContentDeclarationProductType.Average);
  const bestProductsContentDeclaration = getProductPairByType(data, EPDContentDeclarationProductType.BestProduct);
  const worstProductsContentDeclaration = getProductPairByType(data, EPDContentDeclarationProductType.WorstProduct);

  return (
    <CompareLayout fullWidth={fullWidth} setFullWidth={setFullWidth} epdId={epdId}>
      <EpdVersionHistoryDataContext.Provider value={left}>
        <EpdComparedVersionHistoryDataContext.Provider value={right}>
          <PcrV2Context.Provider value={left?.isPcrV2 || right?.isPcrV2}>
            <CompareDiv>
              <StyledEpdNumber>{left?.epdDefinitionInformation.fullIdentificationNumber || '...'}</StyledEpdNumber>
              <StyledEpdNumber>{right?.epdDefinitionInformation.fullIdentificationNumber || '...'}</StyledEpdNumber>
            </CompareDiv>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: 16,
                rowGap: 16,
                marginTop: 16,
                width: '100%',
                gridAutoFlow: 'dense',
              }}
            >
              <CompareGeneralInformationSection data={data} />

              <VersionCompareSection
                data={data}
                displayCondition={(x) => !isFalsyOrEmptyHtmlRichText(x?.additionalInformation?.references)}
                compareBy={(item) => item?.additionalInformation?.references}
              >
                {({ item, compareClassName }) => (
                  <FormPanel
                    style={{
                      marginTop: '0',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                    }}
                  >
                    <FieldPanelHeader style={{ marginBottom: '1rem' }}>References</FieldPanelHeader>
                    <RichTextFrame
                      className={compareClassName}
                      dangerouslySetInnerHTML={{ __html: item?.additionalInformation?.references || '' }}
                    />
                  </FormPanel>
                )}
              </VersionCompareSection>

              <CompareProductsSection data={data} />

              <CompareTechnicalPerformanceSection data={data} />

              {/* Average product*/}
              <ComparePanelComponent title="Content declaration">
                <VersionCompareRow data={data} displayCondition={(x) => !!x}>
                  {({ isLeft }) => (
                    <VersionCompareKeyValueGrid
                      isLeft={isLeft}
                      additionalData={averageProductsContentDeclaration}
                      rowsGetter={getContentDeclarationHeaderRows}
                    />
                  )}
                </VersionCompareRow>

                <VersionCompareRow data={data} displayCondition={(x) => !!x} compareByDom>
                  {({ item, compareClassName }) => (
                    <ProductComponentsGridPdf
                      key={compareClassName}
                      className={compareClassName}
                      epdVersionId={item!.epdVersionId}
                      serviceIncludesProductInSystemBoundary={
                        item!.epdDefinitionInformation.serviceIncludesProductInSystemBoundary
                      }
                      contentDeclarationType={EPDContentDeclarationProductType.Average}
                      isISO21930StandardSelected={IsISO21930StandardCompliant(item!)}
                    />
                  )}
                </VersionCompareRow>
                <VersionCompareRow data={data} displayCondition={(x) => !!x} compareByDom>
                  {({ item, compareClassName }) => (
                    <PackagingMaterialsGridPdf
                      key={compareClassName}
                      className={compareClassName}
                      epdVersionId={item!.epdVersionId}
                      contentDeclarationType={EPDContentDeclarationProductType.Average}
                      isISO21930StandardSelected={IsISO21930StandardCompliant(item!)}
                    />
                  )}
                </VersionCompareRow>
                <VersionCompareRow
                  data={data}
                  displayCondition={(x) => !!x && IsPanelContentEnabled(x.pcrInformation)}
                  compareByDom
                >
                  {({ item, compareClassName }) => (
                    <PanelContentGridPdf
                      className={compareClassName}
                      epdVersionId={item!.epdVersionId}
                      contentDeclarationType={EPDContentDeclarationProductType.Average}
                      isISO21930StandardSelected={IsISO21930StandardCompliant(item!)}
                    />
                  )}
                </VersionCompareRow>

                <VersionCompareRow data={data} displayCondition={(x) => !!x} compareByDom>
                  {({ item, compareClassName }) => (
                    <DangerMaterialsGridPdf
                      className={compareClassName}
                      epdVersionId={item!.epdVersionId}
                      contentDeclarationType={EPDContentDeclarationProductType.Average}
                    />
                  )}
                </VersionCompareRow>
                <VersionCompareRow data={data} displayCondition={(x) => !!x} compareByDom>
                  {({ item, compareClassName }) => (
                    <OtherDangerMaterialsGridPdf
                      className={compareClassName}
                      epdVersionId={item!.epdVersionId}
                      contentDeclarationType={EPDContentDeclarationProductType.Average}
                    />
                  )}
                </VersionCompareRow>
              </ComparePanelComponent>

              {/* best product*/}
              {((data.left?.epdDefinitionInformation?.epdClassification != EPDProductClassificationId.SINGLE_PRODUCT_ID &&
                bestProductsContentDeclaration.left?.productDescription) ||
                (data.right?.epdDefinitionInformation?.epdClassification != EPDProductClassificationId.SINGLE_PRODUCT_ID &&
                  bestProductsContentDeclaration.right?.productDescription)) && (
                <ComparePanelComponent>
                  <VersionCompareRow
                    data={data}
                    displayCondition={(x) =>
                      !!x && x.epdDefinitionInformation?.epdClassification != EPDProductClassificationId.SINGLE_PRODUCT_ID
                    }
                  >
                    {({ item, isLeft }) => <FieldPanelHeader>Content declaration - Best-case product</FieldPanelHeader>}
                  </VersionCompareRow>
                  <VersionCompareRow
                    data={data}
                    displayCondition={(x) =>
                      !!x && x.epdDefinitionInformation?.epdClassification != EPDProductClassificationId.SINGLE_PRODUCT_ID
                    }
                  >
                    {({ item, isLeft }) => (
                      <VersionCompareKeyValueGrid
                        isLeft={isLeft}
                        additionalData={bestProductsContentDeclaration}
                        rowsGetter={getContentDeclarationHeaderRows}
                      />
                    )}
                  </VersionCompareRow>
                  <VersionCompareRow data={data} displayCondition={(x) => !!x} compareByDom>
                    {({ item, compareClassName }) => (
                      <ProductComponentsGridPdf
                        className={compareClassName}
                        epdVersionId={item!.epdVersionId}
                        serviceIncludesProductInSystemBoundary={
                          item!.epdDefinitionInformation.serviceIncludesProductInSystemBoundary
                        }
                        contentDeclarationType={EPDContentDeclarationProductType.BestProduct}
                        isISO21930StandardSelected={IsISO21930StandardCompliant(item!)}
                      />
                    )}
                  </VersionCompareRow>
                  <VersionCompareRow data={data} displayCondition={(x) => !!x} compareByDom>
                    {({ item, compareClassName }) => (
                      <PackagingMaterialsGridPdf
                        className={compareClassName}
                        epdVersionId={item!.epdVersionId}
                        contentDeclarationType={EPDContentDeclarationProductType.BestProduct}
                        isISO21930StandardSelected={IsISO21930StandardCompliant(item!)}
                      />
                    )}
                  </VersionCompareRow>
                  <VersionCompareRow
                    data={data}
                    displayCondition={(x) => !!x && IsPanelContentEnabled(x.pcrInformation)}
                    compareByDom
                  >
                    {({ item, compareClassName }) => (
                      <PanelContentGridPdf
                        className={compareClassName}
                        epdVersionId={item!.epdVersionId}
                        contentDeclarationType={EPDContentDeclarationProductType.BestProduct}
                        isISO21930StandardSelected={IsISO21930StandardCompliant(item!)}
                      />
                    )}
                  </VersionCompareRow>
                  <VersionCompareRow data={data} displayCondition={(x) => !!x} compareByDom>
                    {({ item, compareClassName }) => (
                      <DangerMaterialsGridPdf
                        className={compareClassName}
                        epdVersionId={item!.epdVersionId}
                        contentDeclarationType={EPDContentDeclarationProductType.BestProduct}
                      />
                    )}
                  </VersionCompareRow>
                  <VersionCompareRow data={data} displayCondition={(x) => !!x} compareByDom>
                    {({ item, compareClassName }) => (
                      <OtherDangerMaterialsGridPdf
                        className={compareClassName}
                        epdVersionId={item!.epdVersionId}
                        contentDeclarationType={EPDContentDeclarationProductType.BestProduct}
                      />
                    )}
                  </VersionCompareRow>
                </ComparePanelComponent>
              )}

              {/* worst product*/}
              {((data.left?.epdDefinitionInformation?.epdClassification != EPDProductClassificationId.SINGLE_PRODUCT_ID &&
                worstProductsContentDeclaration.left?.productDescription) ||
                (data.right?.epdDefinitionInformation?.epdClassification != EPDProductClassificationId.SINGLE_PRODUCT_ID &&
                  worstProductsContentDeclaration.right?.productDescription)) && (
                <ComparePanelComponent>
                  <VersionCompareRow
                    data={data}
                    displayCondition={(x) =>
                      !!x && x.epdDefinitionInformation?.epdClassification != EPDProductClassificationId.SINGLE_PRODUCT_ID
                    }
                  >
                    {({ item, isLeft }) => <FieldPanelHeader>Content declaration - Worst-case product</FieldPanelHeader>}
                  </VersionCompareRow>
                  <VersionCompareRow
                    data={data}
                    displayCondition={(x) =>
                      !!x && x.epdDefinitionInformation?.epdClassification != EPDProductClassificationId.SINGLE_PRODUCT_ID
                    }
                  >
                    {({ item, isLeft }) => (
                      <VersionCompareKeyValueGrid
                        isLeft={isLeft}
                        additionalData={worstProductsContentDeclaration}
                        rowsGetter={getContentDeclarationHeaderRows}
                      />
                    )}
                  </VersionCompareRow>
                  <VersionCompareRow data={data} displayCondition={(x) => !!x} compareByDom>
                    {({ item, compareClassName }) => (
                      <ProductComponentsGridPdf
                        className={compareClassName}
                        epdVersionId={item!.epdVersionId}
                        serviceIncludesProductInSystemBoundary={
                          item!.epdDefinitionInformation.serviceIncludesProductInSystemBoundary
                        }
                        contentDeclarationType={EPDContentDeclarationProductType.WorstProduct}
                        isISO21930StandardSelected={IsISO21930StandardCompliant(item!)}
                      />
                    )}
                  </VersionCompareRow>
                  <VersionCompareRow data={data} displayCondition={(x) => !!x} compareByDom>
                    {({ item, compareClassName }) => (
                      <PackagingMaterialsGridPdf
                        className={compareClassName}
                        epdVersionId={item!.epdVersionId}
                        contentDeclarationType={EPDContentDeclarationProductType.WorstProduct}
                        isISO21930StandardSelected={IsISO21930StandardCompliant(item!)}
                      />
                    )}
                  </VersionCompareRow>
                  <VersionCompareRow
                    data={data}
                    displayCondition={(x) => !!x && IsPanelContentEnabled(x.pcrInformation)}
                    compareByDom
                  >
                    {({ item, compareClassName }) => (
                      <PanelContentGridPdf
                        className={compareClassName}
                        epdVersionId={item!.epdVersionId}
                        contentDeclarationType={EPDContentDeclarationProductType.WorstProduct}
                        isISO21930StandardSelected={IsISO21930StandardCompliant(item!)}
                      />
                    )}
                  </VersionCompareRow>
                  <VersionCompareRow data={data} displayCondition={(x) => !!x} compareByDom>
                    {({ item, compareClassName }) => (
                      <DangerMaterialsGridPdf
                        className={compareClassName}
                        epdVersionId={item!.epdVersionId}
                        contentDeclarationType={EPDContentDeclarationProductType.WorstProduct}
                      />
                    )}
                  </VersionCompareRow>
                  <VersionCompareRow data={data} displayCondition={(x) => !!x} compareByDom>
                    {({ item, compareClassName }) => (
                      <OtherDangerMaterialsGridPdf
                        className={compareClassName}
                        epdVersionId={item!.epdVersionId}
                        contentDeclarationType={EPDContentDeclarationProductType.WorstProduct}
                      />
                    )}
                  </VersionCompareRow>
                </ComparePanelComponent>
              )}

              <CompareLcaInformationSection data={data} />

              <CompareSystemBoundarySection data={data} />

              <CompareScenariosSection data={data} />

              <ComparePanelComponent title="Environmental performance">
                <CompareEnvironmentPerformanceSection data={data} />
                <CompareEnvironmentPerformanceCustomSection data={data} />
              </ComparePanelComponent>
            </div>
          </PcrV2Context.Provider>
        </EpdComparedVersionHistoryDataContext.Provider>
      </EpdVersionHistoryDataContext.Provider>
    </CompareLayout>
  );
};

const RichTextFrame = styled.div`
  border-style: solid;
  border-width: 1px;
  border-color: ${(props) => props.theme.colors.elementsInputBorder};
  border-radius: 4px;
  padding-left: 21px;
  padding-right: 21px;
  word-break: break-word;
  height: 100%;
`;

const StyledEpdNumber = styled.div`
  ${(props) => props.theme.typography.headerH4};
  font-size: 18px;
  margin-top: 11px;
`;

function getProductPairByType<T extends { type: EPDContentDeclarationProductType }>(
  data: {
    left?: { contentDeclarationProducts?: T[] } | null;
    right?: { contentDeclarationProducts?: T[] } | null;
  },
  productType: EPDContentDeclarationProductType
): { left: T; right: T } {
  const findProduct = (side?: { contentDeclarationProducts?: T[] } | null) =>
    side?.contentDeclarationProducts?.find((x) => x.type === productType) ?? null;

  const left = findProduct(data.left);
  const right = findProduct(data.right);

  return {
    left: (left ? { ...left, type: productType } : { type: productType }) as T,
    right: (right ? { ...right, type: productType } : { type: productType }) as T,
  };
}

export default withGreyBackground(EpdCompilerVersionCompare);
